import React, { useMemo, useEffect, useLayoutEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link/link';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { getAdjustedTopPropertyValue } from './utils/getAdjustedTopPropertyValue.ts';
import { BannerContainer, Banner, IconContainer } from './styled/index.ts';
import type { EditorBannerProps, EditorBannerContainerProps } from './OnboardingEditorBanner.tsx';

const i18n = defineMessages({
	bannerMessage: {
		id: 'onboarding-editor.draft.banner',
		defaultMessage:
			'{boldedUnpublishedDraftText} Only you can see it until you publish it or give someone the link. {learnMoreLink}',
		description:
			'This is the main message shown in the draft banner. It informs users that the page is an unpublished draft. The placeholder {boldedUnpublishedDraftText} will be substituted with "This is an unpublished draft." in bold text, and {learnMoreLink} will be replaced with a "Learn more" link to documentation about page restrictions.',
	},
	unpublishedDraftText: {
		id: 'onboarding-editor.unpublished.draft',
		defaultMessage: '<b>This is an unpublished draft.</b>',
		description:
			'Bold text displayed in the draft banner informing users that the current page is an unpublished draft. This appears as part of the banner message shown at the top of the editor.',
	},
	learnMore: {
		id: 'onboarding-editor.learn.more',
		defaultMessage: 'Learn more',
		description:
			'Text for the hyperlink in the draft banner that opens documentation about page restrictions. When clicked, it navigates to Atlassian support documentation explaining draft pages and visibility settings.',
	},
});

export const EditorDraftBanner = (
	props: EditorBannerProps & EditorBannerContainerProps,
): React.JSX.Element => {
	const {
		isContentCreationPanelOpen,
		showingOfflineBanner,
		setIsVisible,
		deserializedData: { wasDraftBannerSeen } = {},
		setIsOnboardingBannerDataLoaded,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	useLayoutEffect(() => {
		if (!wasDraftBannerSeen) {
			const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

			if (editorNode) {
				editorNode.style.marginTop = '52px';
			}

			return () => {
				if (editorNode) editorNode.style.marginTop = '0';
			};
		}
	}, [wasDraftBannerSeen]);

	useEffect(() => {
		if (!wasDraftBannerSeen) {
			void setOnboardingState([
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.WAS_DRAFT_BANNER_SEEN,
					value: 'true',
				},
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.IS_DRAFT_BANNER_VISIBLE,
					value: 'true',
				},
			]);

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'onboardingEditorDraft',
				},
			}).fire();
			// TODO: Refactor https://hello.atlassian.net/browse/CONFONBO-1674
			setTimeout(() => {
				setIsOnboardingBannerDataLoaded(true);
			}, 0);
		}
	}, [
		createAnalyticsEvent,
		setIsOnboardingBannerDataLoaded,
		setOnboardingState,
		wasDraftBannerSeen,
	]);

	const _onDismiss = () => {
		setIsVisible(false);
		// Move all the content back up 52px to adjust for the banner disappearing
		const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

		if (editorNode) {
			editorNode.style.marginTop = '0px';
		}
	};

	// compiled shouldn't handle a complicated runtime CSS, hence the inlined style
	const calculatedTop = useMemo(
		() => getAdjustedTopPropertyValue(showingOfflineBanner),
		[showingOfflineBanner],
	);

	return (
		<BannerContainer
			style={{ top: calculatedTop }}
			isContentCreationPanelOpen={isContentCreationPanelOpen}
			data-testid="onboarding-editor-draft-banner"
		>
			<Banner>
				<FormattedMessage
					{...i18n.bannerMessage}
					values={{
						boldedUnpublishedDraftText: (
							<FormattedMessage
								{...i18n.unpublishedDraftText}
								values={{ b: (chunks) => <b>{chunks}</b> }}
							/>
						),
						learnMoreLink: (
							<Link
								href="https://support.atlassian.com/confluence-cloud/docs/add-or-remove-page-restrictions/"
								target="_blank"
							>
								<FormattedMessage {...i18n.learnMore} />
							</Link>
						),
					}}
				/>
				<IconContainer data-testid="onboarding-editor-close-banner" onClick={_onDismiss}>
					<CrossIcon label="dismiss" />
				</IconContainer>
			</Banner>
		</BannerContainer>
	);
};
